import { Button, MenuItem, Intent, InputGroup, Tooltip, Position } from '@blueprintjs/core';
import { inject, observer } from 'mobx-react';
import { Instance } from 'mobx-state-tree';
import React from 'react';
import { Page, TLCode } from '../../common/enums';
import {
  PharmacyDetailedStore,
  PharmacyStore,
  GroupStore,
  AnchorStore,
  PharmacyStatusStore,
  AuthenticationStore,
  ApplicationStore,
  DutyOffRequestStore
} from '../../store';
import { Pharmacy, Secretariat } from '../../store/models';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import styled from '@emotion/styled';
import { IItemRendererProps, MultiSelect } from '@blueprintjs/select';
import { IconNames } from '@blueprintjs/icons';
import {
  DataGrid,
  Column,
  Button as DGButton,
  Sorting,
  SearchPanel,
  DataGridTypes,
  Export,
  FilterRow,
  FilterPanel,
  HeaderFilter,
  GroupPanel,
  Grouping,
  ColumnChooser,
  Pager,
  Paging,
} from 'devextreme-react/data-grid';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { PharmacyStatusCell } from '../common/grid/PharmacyStatusCell';

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: start;
  gap: 20px;
  align-content: center;
`;

const DivColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const DivRightColumn = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: auto;
`;

interface SecretariatPharmaciesTabProps extends WithTranslateProps {
  readOnly: boolean;
  secretariat?: Instance<typeof Secretariat>;
  PharmacyStore?: Instance<typeof PharmacyStore>;
  PharmacyDetailedStore?: Instance<typeof PharmacyDetailedStore>;
  GroupStore?: Instance<typeof GroupStore>;
  AnchorStore?: Instance<typeof AnchorStore>;
  PharmacyStatusStore?: Instance<typeof PharmacyStatusStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  ApplicationStore?: Instance<typeof ApplicationStore>;
  DutyOffRequestStore?: Instance<typeof DutyOffRequestStore>;
  refreshSecretariatPharmacyGeodata: (secretariatId: number) => void;
}

interface SecretariatPharmaciesTabState {
  isLoading: boolean;
  pharmaciesToLink: Array<Instance<typeof Pharmacy>>;
  checkStartYear: string;
}

@inject('PharmacyStore')
@inject('PharmacyDetailedStore')
@inject('GroupStore')
@inject('AnchorStore')
@inject('PharmacyStatusStore')
@inject('AuthenticationStore')
@inject('ApplicationStore')
@inject('DutyOffRequestStore')
@observer
class SecretariatPharmaciesTab extends React.Component<SecretariatPharmaciesTabProps, SecretariatPharmaciesTabState> {
  state = {
    isLoading: false,
    pharmaciesToLink: [],
    checkStartYear: new Date().getFullYear().toString()
  } as SecretariatPharmaciesTabState;

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    this._isMounted && this.setState({ isLoading: true });
    await this.props.GroupStore!.checkListLoaded();
    await this.props.AnchorStore!.checkListLoaded();
    await this.props.PharmacyStore!.checkListLoaded();
    this._isMounted && this.setState({ isLoading: false });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  private pharmaciesWithoutSecretariat = () => {
    return this.props
      .PharmacyStore!.dataList.filter((elem) => {
        return elem.secretariatId === undefined;
      })
      .filter((elem) => this.state.pharmaciesToLink.findIndex((p) => p.pharmacyId === elem.pharmacyId) === -1);
  };

  private pharmaciesItemRenderer = (
    item: Instance<typeof Pharmacy>,
    { handleClick, modifiers }: IItemRendererProps,
  ) => {
    if (!modifiers.matchesPredicate) return null;
    return (
      <MenuItem
        active={modifiers.active}
        key={item.pharmacyId}
        onClick={handleClick}
        text={item.apbNumber.toString() + ' - ' + item.name + ' - ' + item.city + ' (' + item.zipCode + ')'}
      />
    );
  };

  private onPharmacySelected = (item: Instance<typeof Pharmacy>) => {
    this.setState({ pharmaciesToLink: [...this.state.pharmaciesToLink, item] });
  };

  private onPharmacyRemoved = (value: string, index: number) => {
    this.setState({
      pharmaciesToLink: this.state.pharmaciesToLink.filter((elem) => elem.apbNumber.toString() + ' - ' + elem.name + ' - ' + elem.city + ' (' + elem.zipCode + ')' !== value),
    });
  };

  private linkSelectedPharmaciesToSecretariat = () => {
    if (this.props.secretariat !== undefined)
      this.state.pharmaciesToLink.forEach(async (element) => {
        await this.props.PharmacyDetailedStore!.getByIdWithDetails(element.pharmacyId);
        const pharmacyToSave = this.props.PharmacyDetailedStore!.getById(element.pharmacyId);
        this.props.PharmacyDetailedStore!.saveElement({
          ...pharmacyToSave,
          secretariatId: this.props.secretariat!.secretariatId,
        });
      });
      this.setState({ pharmaciesToLink: [] });
  };

  private buildGridData = () => {
    return this.props
      .PharmacyStore!.dataList.filter((elem) => {
        return this.props.secretariat !== undefined && elem.secretariatId === this.props.secretariat!.secretariatId;
      })
      .map((p) => ({
        pharmacyId: p.pharmacyId,
        level: p.level,
        apbNumber: p.apbNumber,
        name: p.name,
        address: p.address,
        city: p.city,
        zipCode: p.zipCode,
        statusId: p.statusId,
        group:
          p.groupId === undefined ? '' : this.props.GroupStore!.dataList.find((g) => g.groupId === p.groupId)!.name,
        //anchor: p.anchor,
        anchor: p.anchorId === undefined ? '' : this.props.AnchorStore!.dataList.find((a) => a.anchorId === p.anchorId!)!.name,
        secretariatId: p.secretariatId,
      }));
  };

  private refreshGeodata = () => {
    this.props.refreshSecretariatPharmacyGeodata(this.props.secretariat!.secretariatId);
  };

  displayPharmacyStatusCell = (params: any) => {
    const pharmacyStatus = this.props.PharmacyStatusStore!.dataList.find(
      (ps) => ps.pharmacyStatusId === params.row.data.statusId,
    );
    if (pharmacyStatus === undefined) return <></>;
    return <PharmacyStatusCell pharmacyStatus={pharmacyStatus} />;
  };

  onExporting = (e: DataGridTypes.ExportingEvent) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Main sheet');

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(
          new Blob([buffer], { type: 'application/octet-stream' }),
          'Pharmacies_of_secretariat_' + this.props.secretariat!.name + '.xlsx',
        );
      });
    });
  };

  onRowClicked = (ev) => {
    this.props.ApplicationStore!.navigate(`${Page.PharmacyPage}/${ev.data.pharmacyId}`);
  }

  checkDutyOffRequests = async () => {
    this._isMounted && this.setState({ isLoading: true });
    await this.props.DutyOffRequestStore!.checkPharmacyDutyOffRequestsBySecretariat(
      this.state.checkStartYear,
      this.props.secretariat!.secretariatId.toString(),
    );
    this._isMounted && this.setState({ isLoading: false });
  };
  
  onCheckStartYearChanged = (value) => {
    this.setState({ checkStartYear: value });
  };

  private recalculateDefaultExchangeFlags  = () => {
    this.props.PharmacyStore!.recalculateDefaultExchangeFlagsBySecretariat(this.props.secretariat!.secretariatId);
  };
   
  private reapplyDefaultExchangeFlags  = () => {
    this.props.PharmacyStore!.reapplyDefaultExchangeFlagsBySecretariat(this.props.secretariat!.secretariatId);
  };
    
  private recalculateDefaultPairingFlags  = () => {
    this.props.PharmacyStore!.recalculateDefaultPairingFlagsBySecretariat(this.props.secretariat!.secretariatId);
  };
    
  private reapplyDefaultPairingFlags  = () => {
    this.props.PharmacyStore!.reapplyDefaultPairingFlagsBySecretariat(this.props.secretariat!.secretariatId);
  };

  render() {
    const { translate } = this.props;
    return (
      <Container>
        <DivLine>
          {!this.props.readOnly ? (
            <>
              <DivColumn>
                <DivLine>
                  <MultiSelect
                    itemRenderer={this.pharmaciesItemRenderer}
                    items={this.pharmaciesWithoutSecretariat()}
                    onItemSelect={this.onPharmacySelected}
                    tagRenderer={(item: Instance<typeof Pharmacy>) => item.apbNumber.toString() + ' - ' + item.name + ' - ' + item.city + ' (' + item.zipCode + ')'}
                    itemPredicate={(query, item) => {
                      return (
                        item.apbNumber.toString().toLocaleLowerCase().includes(query.toLocaleLowerCase()) ||
                        item.name.toLocaleLowerCase().includes(query.toLocaleLowerCase()) ||
                        (item.city ? item.city.toLocaleLowerCase().includes(query.toLocaleLowerCase()) : false) ||
                        (item.zipCode ? item.zipCode.toLocaleLowerCase().includes(query.toLocaleLowerCase()) : false)
                      );
                    }}
                    tagInputProps={{
                      onRemove: this.onPharmacyRemoved,
                    }}
                    selectedItems={this.state.pharmaciesToLink}
                    resetOnSelect
                    placeholder="Select Pharmacies to link"
                  />
                  <Button
                    onClick={this.linkSelectedPharmaciesToSecretariat}
                    text={this.props.translate(TLCode.SECRETARIAT_LinkPharmacy)}
                    disabled={this.state.pharmaciesToLink.length === 0}
                  />
                </DivLine>
              </DivColumn>
            </>
          ) : (
            <></>
          )}
          {this.props.AuthenticationStore!.isSuperAdmin && (
            <DivRightColumn>
              <DivLine>
              <Button
                text={translate(TLCode.GROUP_RefreshGeodata)}
                icon={IconNames.REFRESH}
                intent={Intent.DANGER}
                onClick={this.refreshGeodata}
              />
              <InputGroup
                placeholder="year to start checking"
                value={this.state.checkStartYear}
                onChange={(e) => this.onCheckStartYearChanged(e.target.value)}
              />
              <Button
                icon={IconNames.REFRESH}
                intent={Intent.DANGER}
                onClick={this.checkDutyOffRequests}
                text={translate(TLCode.GROUP_CheckDutyOffRequests)}
              />
              </DivLine>
            </DivRightColumn>
          )}
        </DivLine>
        <DivLine>
          <DivColumn>
            <div style={{ display:'flex', paddingTop:'5px'}}>
              <b>{translate(TLCode.PHARMACY_RecalculateExchangeAllowed_Lbl)}</b>&nbsp;
              <Tooltip
                                      content={translate(TLCode.PHARMACY_RecalculateDefaultExchangeFlags_ToolTip)}
                                      position={Position.BOTTOM}
                                      hoverOpenDelay={500}
              >
                <Button
                                      intent={Intent.PRIMARY}
                                      onClick={this.recalculateDefaultExchangeFlags}
                                      text={translate(TLCode.PHARMACY_RecalculateDefaultExchangeFlags_Btn)}
                                    />
              </Tooltip>
              &nbsp;&nbsp;
              <Tooltip
                                      content={translate(TLCode.PHARMACY_ReapplyDefaultExchangeFlags_ToolTip)}
                                      position={Position.BOTTOM}
                                      hoverOpenDelay={500}
              >
                <Button
                                    intent={Intent.DANGER}
                                    onClick={this.reapplyDefaultExchangeFlags}
                                    text={translate(TLCode.PHARMACY_ReapplyDefaultExchangeFlags_Btn)}
                                  />
              </Tooltip>
            </div>
          </DivColumn>
          <DivColumn>
            <div style={{ display:'flex', paddingTop:'5px'}}>
              <b>{translate(TLCode.PHARMACY_RecalculatePairingAllowed_Lbl)}</b>&nbsp;
              <Tooltip
                                content={translate(TLCode.PHARMACY_RecalculateDefaultPairingFlags_ToolTip)}
                                position={Position.BOTTOM}
                                hoverOpenDelay={500}
                              >
                                                          <Button
                                                            intent={Intent.PRIMARY}
                                                            onClick={this.recalculateDefaultPairingFlags}
                                                            text={translate(TLCode.PHARMACY_RecalculateDefaultPairingFlags_Btn)}
                                                          />
              </Tooltip>
              &nbsp;&nbsp;
              <Tooltip
                                                            content={translate(TLCode.PHARMACY_ReapplyDefaultPairingFlags_ToolTip)}
                                                            position={Position.BOTTOM}
                                                            hoverOpenDelay={500}
                                                        >
                                                        <Button
                                                          intent={Intent.DANGER}
                                                          onClick={this.reapplyDefaultPairingFlags}
                                                          text={translate(TLCode.PHARMACY_ReapplyDefaultExchangeFlags_Btn)}
                                                        />
              </Tooltip>
            </div>
          </DivColumn>
        </DivLine>
        <br />
        <br />
        <DataGrid
          id="pharmaciesList"
          dataSource={this.buildGridData()}
          showBorders
          onExporting={this.onExporting}
          onRowClick={this.onRowClicked}
          allowColumnResizing
          rowAlternationEnabled={true}

        >
          <Export enabled={true} texts={{ exportAll: translate(TLCode.GRID_EXCEL_EXPORT) }} />
          <Sorting mode="multiple" />
          <FilterPanel visible={true} />
          <HeaderFilter visible={true} />
          <FilterRow visible={true} applyFilter={'auto'} />
          <GroupPanel visible={true} />
          <SearchPanel visible={true} highlightCaseSensitive={true} width={400} />
          <Grouping autoExpandAll={false} />
          <ColumnChooser enabled={true} />
          <Column dataType="number" dataField="level" caption="" width={40} />
          <Column
            dataType="number"
            dataField="apbNumber"
            caption={translate(TLCode.PHARMACIES_ApbNum)}
            width={80}
            defaultSortOrder="asc"
            sortIndex={1}
          />
          <Column dataType="string" dataField="name" caption={translate(TLCode.PHARMACIES_PharmacyName)} />
          <Column dataType="string" dataField="address" caption={translate(TLCode.PHARMACIES_Address)} />
          <Column dataType="string" dataField="zipCode" caption={translate(TLCode.PHARMACIES_ZipCode)} width={55} />
          <Column dataType="string" dataField="city" caption={translate(TLCode.PHARMACIES_City)} width={180} />
          <Column
            dataType="string"
            dataField="group"
            caption={translate(TLCode.PHARMACIES_Group)}
            width={80}
            defaultSortOrder="asc"
            sortIndex={0}
          />
          <Column dataType="string" dataField="anchor" caption={translate(TLCode.PHARMACIES_Anchor)} width={60} />
          <Column
            cellRender={this.displayPharmacyStatusCell}
            caption={translate(TLCode.PHARMACY_Status)}
            width={60}
            alignment="center"
          />
          <Pager
            visible={true}
            allowedPageSizes={[5, 10, 20, 50, 100]}
            showPageSizeSelector={true}
            showInfo={true}
            showNavigationButtons={true}
          />
          <Paging defaultPageSize={20} />
        </DataGrid>
      </Container>
    );
  }
}

export default withTranslate(SecretariatPharmaciesTab);
